import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AppRoutes } from 'constants/AppRoutes';
import isLaunchpadTileVcmGated from 'constants/launchpadVcmGating';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import { selectPillarByKey } from 'store/slices/user/selectors';

import type { TLaunchpadLaneId } from 'constants/interfaces/ILaunchpadLane';
import type ILaunchpadTile from 'constants/interfaces/ILaunchpadLane';

interface IUseLaunchpadTileAction {
  laneId: TLaunchpadLaneId,
  tile: ILaunchpadTile,
  hasActiveVcm: boolean,
  selectedMapId?: string,
  onDiscoverClick?: () => void,
}

// Shared tile behaviour (VCM gating, discover, navigate, disabled toast) + pillar-access visibility.
export default function useLaunchpadTileAction({
  laneId,
  tile,
  hasActiveVcm,
  selectedMapId,
  onDiscoverClick,
}: IUseLaunchpadTileAction): { handleOpen: () => void, isHidden: boolean } {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pillar } = useUserMeQuery(undefined, {
    selectFromResult: (result) => ({
      pillar: tile.pillarKey
        ? selectPillarByKey({ data: result.data, pillarKey: tile.pillarKey })
        : undefined,
    }),
  });

  const isHidden = Boolean(tile.status === 'live' && tile.pillarKey && !pillar?.canView);

  const handleOpen = useCallback(() => {
    if (isLaunchpadTileVcmGated(laneId, tile.id) && !hasActiveVcm) {
      toast.info(t('launchpad.mustCreateValueChain'));
      return;
    }

    if (tile.action === 'discover') {
      onDiscoverClick?.();
      return;
    }

    // The map card opens the selected value chain map directly, not the maps list.
    if (tile.id === 'map') {
      navigate(
        selectedMapId
          ? generatePath(AppRoutes.companyMap, { valueChainMapId: selectedMapId })
          : AppRoutes.valueChainMap,
      );
      return;
    }

    if (tile.status === 'live' && tile.route) {
      navigate(tile.route);
      return;
    }

    toast.info(
      t('launchpad.tileDisabled', {
        name: t(tile.i18nKey),
        defaultValue: '{{name}} — Module disabled: Please contact Customer Success',
      }),
    );
  }, [hasActiveVcm, laneId, navigate, onDiscoverClick, selectedMapId, t, tile]);

  return { handleOpen, isHidden };
}
